import React, { useState, useEffect } from "react";
import { CheckCircle2, MinusCircle, ChevronDown, ChevronUp } from "lucide-react";

/* ─── colors ─────────────────────────────────────────────── */
const LITE_COLOR  = "#EF9F27";
const FULL_COLOR  = "#1D9E75";
const ACC_COLOR   = "#059669";
const LITE_PRO_COLOR = "#378ADD";

/* ─── API plan shape (from /api/plans) ───────────────────── */
interface ApiPlan {
  planId: string;
  label: string;
  priceMonthly: number | null;
  priceYearly: number | null;
  maxProjects: number | null;
  maxUsers: number | null;
  popular: boolean;
  isActive: boolean;
}

/* ─── Static display metadata per plan ───────────────────── */
interface PlanMeta {
  track: "lite" | "full" | "accounting";
  tagline: string;
  accentColor: string;
  trackLabel: string;
  trackLabelColor: string;
  ctaStyle: "lite-outline" | "lite-filled" | "full-outline" | "full-filled" | "acc-filled";
  defaultBadge?: string;
  features: { text: string; highlight?: boolean; muted?: boolean }[];
}

/* ─── Merged render plan ─────────────────────────────────── */
interface PricingPlan {
  id: string;
  track: "lite" | "full" | "accounting";
  label: string;
  tagline: string;
  monthly: number;
  yearlyTotal: number;
  projects: number | null;
  users: number | null;
  features: { text: string; highlight?: boolean; muted?: boolean }[];
  cta: string;
  ctaStyle: "lite-outline" | "lite-filled" | "full-outline" | "full-filled" | "acc-filled";
  badge?: string;
  popular?: boolean;
  accentColor: string;
  trackLabel: string;
  trackLabelColor: string;
}

/* ─── Static display metadata keyed by planId ────────────── */
const PLAN_META: Record<string, PlanMeta> = {
  "lite": {
    track: "full",
    tagline: "For established contractors managing active projects with invoicing, VOs, and cash flow",
    accentColor: LITE_COLOR,
    trackLabel: "Full finance",
    trackLabelColor: LITE_COLOR,
    ctaStyle: "lite-outline",
    features: [
      { text: "Progress invoices & interim valuations" },
      { text: "BOQ + variation orders & descopes", highlight: true },
      { text: "Subcontractor & supplier management" },
      { text: "Cash flow tracking & payroll" },
      { text: "Custom document numbering" },
    ],
  },
  "lite-pro": {
    track: "full",
    tagline: "For growing finance teams who need reporting, CFO view, and retention management",
    accentColor: LITE_PRO_COLOR,
    trackLabel: "Full finance",
    trackLabelColor: LITE_PRO_COLOR,
    ctaStyle: "full-outline",
    features: [
      { text: "Everything in Lite" },
      { text: "Financial Reports & Analytics", highlight: true },
      { text: "CFO View Dashboard", highlight: true },
      { text: "Retention Management & Release", highlight: true },
      { text: "Promissory Notes / PDC Tracking", highlight: true },
    ],
  },
  "pro": {
    track: "full",
    tagline: "For finance teams managing complex portfolios with multi-company, API access, and custom branding",
    accentColor: FULL_COLOR,
    trackLabel: "Full finance",
    trackLabelColor: FULL_COLOR,
    ctaStyle: "full-filled",
    features: [
      { text: "Everything in Lite Pro" },
      { text: "Multi-company workspace", highlight: true },
      { text: "API access & integrations", highlight: true },
      { text: "Custom branding & white-label documents" },
      { text: "VAT configuration & advanced tax" },
    ],
  },
  "pro-plus": {
    track: "accounting",
    tagline: "All features plus a dedicated double-entry accounting ledger — for accounting teams and CFOs",
    accentColor: ACC_COLOR,
    trackLabel: "Full finance + Accounting",
    trackLabelColor: ACC_COLOR,
    ctaStyle: "acc-filled",
    defaultBadge: "All features",
    features: [
      { text: "Everything in Pro" },
      { text: "Chart of Accounts (pre-seeded COA)", highlight: true },
      { text: "Double-entry journal entries", highlight: true },
      { text: "General Ledger with running balance", highlight: true },
      { text: "Trial Balance, Balance Sheet & Income Statement" },
      { text: "Auto-posting from transaction types" },
    ],
  },
};

/* ─── Merge API plan + local metadata into render plan ───── */
function mergeApiPlan(api: ApiPlan): PricingPlan | null {
  const meta = PLAN_META[api.planId];
  if (!meta) return null;
  const monthly = api.priceMonthly ?? 0;
  return {
    id: api.planId,
    track: meta.track,
    label: api.label,
    tagline: meta.tagline,
    monthly,
    yearlyTotal: api.priceYearly ?? monthly * 10,
    projects: api.maxProjects ?? null,
    users: api.maxUsers ?? null,
    features: meta.features,
    cta: "Start free — 14 days",
    ctaStyle: meta.ctaStyle,
    badge: api.popular ? "Most popular" : meta.defaultBadge,
    popular: api.popular,
    accentColor: meta.accentColor,
    trackLabel: meta.trackLabel,
    trackLabelColor: meta.trackLabelColor,
  };
}

/* ─── price helpers ──────────────────────────────────────── */
function getDisplayPrice(plan: PricingPlan, billing: "monthly" | "annual") {
  const yearlyPerMonth = +(plan.yearlyTotal / 12).toFixed(2);
  if (billing === "monthly") {
    return {
      main:    `$${plan.monthly}`,
      subtext: `$${plan.yearlyTotal}/yr billed annually`,
    };
  }
  return {
    main:    `$${yearlyPerMonth}`,
    subtext: `$${plan.yearlyTotal}/yr · 2 months free`,
  };
}

/* ─── feature comparison table ───────────────────────────── */
type CellValue = true | false | string;

interface CompGroup {
  label: string;
  rows: { label: string; values: CellValue[] }[];
}

const COL_PLANS = ["Lite","Lite Pro","Pro","Pro Plus"];

const COMPARISON_GROUPS: CompGroup[] = [
  {
    label: "LIMITS",
    rows: [
      { label: "Projects",         values: ["15","35","75","120"] },
      { label: "Users",            values: ["10","15","25","Unlimited"] },
      { label: "File attachments", values: [true, true, true, true] },
    ],
  },
  {
    label: "FINANCE & DOCUMENTS",
    rows: [
      { label: "Progress invoices",       values: [true,  true,  true,  true]  },
      { label: "Quotations & proposals",  values: [true,  true,  true,  true]  },
      { label: "Purchase orders",         values: [true,  true,  true,  true]  },
      { label: "Itemized BOQ",            values: [true,  true,  true,  true]  },
      { label: "BOQ + variation orders",  values: [true,  true,  true,  true]  },
      { label: "Subcontractor certs",     values: [true,  true,  true,  true]  },
      { label: "Custom doc numbering",    values: [true,  true,  true,  true]  },
      { label: "Print & PDF export",      values: [true,  true,  true,  true]  },
    ],
  },
  {
    label: "ADVANCED PROJECT FINANCE",
    rows: [
      { label: "Cash flow tracking",     values: [true,  true,  true,  true]  },
      { label: "Financial reports",      values: [false, "Full", "Full", "Full"] },
      { label: "CFO View Dashboard",     values: [false, true,  true,  true]  },
      { label: "Retention management",   values: [false, true,  true,  true]  },
      { label: "PDC / promissory notes", values: [false, true,  true,  true]  },
      { label: "VAT configuration",      values: [false, false, true,  true]  },
    ],
  },
  {
    label: "PLATFORM",
    rows: [
      { label: "Subcontractor mgmt",  values: [true,  true,  true,  true]  },
      { label: "Supplier management", values: [true,  true,  true,  true]  },
      { label: "Payroll & staff",     values: [true,  true,  true,  true]  },
      { label: "Team invitations",    values: [true,  true,  true,  true]  },
      { label: "Project teams",       values: [true,  true,  true,  true]  },
      { label: "Multi-company",       values: [false, false, true,  true]  },
      { label: "API access",          values: [false, false, true,  true]  },
      { label: "Custom branding",     values: [false, false, true,  true]  },
    ],
  },
  {
    label: "ACCOUNTING MODULE",
    rows: [
      { label: "Double-entry journal entries", values: [false, false, false, true] },
      { label: "Chart of Accounts (COA)",      values: [false, false, false, true] },
      { label: "General Ledger",               values: [false, false, false, true] },
      { label: "Trial Balance",                values: [false, false, false, true] },
      { label: "Balance Sheet & P&L",          values: [false, false, false, true] },
      { label: "Auto-posting from docs",       values: [false, false, false, true] },
    ],
  },
];

function Cell({ v }: { v: CellValue }) {
  if (v === true)  return <CheckCircle2 className="w-4 h-4 mx-auto text-emerald-500" />;
  if (v === false) return <MinusCircle  className="w-4 h-4 mx-auto text-slate-200" />;
  const isPositive = ["Full","Standard","Unlimited"].includes(v as string) || /^\d+$/.test(v as string);
  const isBasic    = ["Basic","Snapshot"].includes(v as string);
  const isBlue     = ["Unlimited"].includes(v as string) || /^\d+$/.test(v as string);
  if (isBlue)    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background:"#E6F1FB", color:"#378ADD" }}>{v}</span>;
  if (isPositive) return <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background:"#E1F5EE", color:"#1D9E75" }}>{v}</span>;
  if (isBasic)    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background:"#FAEEDA", color:"#EF9F27" }}>{v}</span>;
  return <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background:"#E6F1FB", color:"#378ADD" }}>{v}</span>;
}

/* ─── CTA button helper ──────────────────────────────────── */
function CtaButton({ plan, onTryDemo }: { plan: PricingPlan; onTryDemo: (id: string) => void }) {
  const base = "w-full text-center font-semibold py-2.5 rounded-xl transition-all text-sm cursor-pointer block";
  const style = plan.ctaStyle;

  if (style === "lite-filled") {
    return (
      <button
        onClick={() => onTryDemo(plan.id)}
        className={`${base} text-white`}
        style={{ background: LITE_COLOR }}
      >{plan.cta}</button>
    );
  }
  if (style === "lite-outline") {
    return (
      <button
        onClick={() => onTryDemo(plan.id)}
        className={`${base} border-2 bg-transparent`}
        style={{ borderColor: LITE_COLOR, color: LITE_COLOR }}
      >{plan.cta}</button>
    );
  }
  if (style === "full-filled") {
    return (
      <button
        onClick={() => onTryDemo(plan.id)}
        className={`${base} text-white`}
        style={{ background: FULL_COLOR }}
      >{plan.cta}</button>
    );
  }
  if (style === "full-outline") {
    return (
      <button
        onClick={() => onTryDemo(plan.id)}
        className={`${base} border-2 bg-transparent`}
        style={{ borderColor: FULL_COLOR, color: FULL_COLOR }}
      >{plan.cta}</button>
    );
  }
  if (style === "acc-filled") {
    return (
      <button
        onClick={() => onTryDemo(plan.id)}
        className={`${base} text-white`}
        style={{ background: ACC_COLOR }}
      >{plan.cta}</button>
    );
  }
  return null;
}

/* ─── single plan card ───────────────────────────────────── */
function PlanCard({ plan, billing, onTryDemo }: {
  plan: PricingPlan;
  billing: "monthly" | "annual";
  onTryDemo: (id: string) => void;
}) {
  const { main, subtext } = getDisplayPrice(plan, billing);
  const isPro   = plan.id === "pro";
  const isProPlus = plan.id === "pro-plus";
  const border  = isPro
    ? `2px solid ${FULL_COLOR}`
    : isProPlus
    ? `2px solid ${ACC_COLOR}`
    : "1px solid #e2e8f0";

  return (
    <div
      className="rounded-2xl flex flex-col relative overflow-hidden bg-white"
      style={{ border, boxShadow: isPro ? `0 4px 24px ${FULL_COLOR}28` : isProPlus ? `0 4px 24px ${ACC_COLOR}28` : "0 1px 4px rgba(0,0,0,0.06)" }}
    >
      {/* top accent bar */}
      <div className="h-1 w-full" style={{ background: plan.accentColor }} />

      {/* badge (Most popular, Best value) */}
      {plan.badge && (
        <div
          className="absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full text-white"
          style={{ background: plan.accentColor }}
        >{plan.badge}</div>
      )}

      <div className="p-6 flex flex-col flex-1">
        {/* track label */}
        <div className="text-xs font-semibold mb-1 tracking-wide" style={{ color: plan.trackLabelColor }}>
          {plan.trackLabel}
        </div>

        {/* plan name */}
        <div className="text-lg font-bold text-slate-900 mb-1">{plan.label}</div>

        {/* tagline */}
        <div className="text-xs text-slate-500 leading-relaxed mb-3">{plan.tagline}</div>

        {/* price */}
        <div className="mb-1">
          <span className="text-3xl font-extrabold text-slate-900">{main}</span>
          <span className="text-sm text-slate-500 ml-1">/mo</span>
        </div>
        <div className="text-xs text-slate-400 mb-4">{subtext}</div>

        {/* limits */}
        <div className="flex gap-3 mb-5">
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{ background: "#E6F1FB", color: "#378ADD" }}
          >
            {`${plan.projects === null || plan.projects === -1 ? "Unlimited" : plan.projects} project${plan.projects !== 1 ? "s" : ""}`}
          </span>
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{ background: "#E6F1FB", color: "#378ADD" }}
          >
            {`${plan.users === null || plan.users === -1 ? "Unlimited" : plan.users} user${plan.users !== 1 ? "s" : ""}`}
          </span>
        </div>

        {/* features */}
        <div className="space-y-2.5 flex-1 mb-6">
          {plan.features.map((f, i) => (
            <div key={i} className={`flex items-start gap-2 text-sm ${f.muted ? "opacity-40" : ""}`}>
              <CheckCircle2
                className="w-4 h-4 mt-0.5 shrink-0"
                style={{ color: f.muted ? "#94a3b8" : plan.accentColor }}
              />
              <span
                className={f.muted ? "italic text-slate-400" : f.highlight ? "font-semibold text-slate-800" : "text-slate-600"}
              >{f.text}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <CtaButton plan={plan} onTryDemo={onTryDemo} />
      </div>
    </div>
  );
}

/* ─── main export ────────────────────────────────────────── */
export function PricingSection({ onTryDemo, onContactSales }: {
  onTryDemo: (planId: string) => void;
  onContactSales?: () => void;
}) {
  const [billing,     setBilling]     = useState<"monthly" | "annual">("monthly");
  const [track,       setTrack]       = useState<"all" | "lite" | "full" | "accounting">("all");
  const [compareOpen, setCompareOpen] = useState(false);
  const [plans,       setPlans]       = useState<PricingPlan[]>([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    const base = import.meta.env.BASE_URL.replace(/\/$/, "");
    fetch(`${base}/api/plans`)
      .then(r => r.json() as Promise<ApiPlan[]>)
      .then(apiPlans => {
        const merged = apiPlans
          .map(mergeApiPlan)
          .filter((p): p is PricingPlan => p !== null);
        setPlans(merged.length > 0 ? merged : []);
      })
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const corePlans       = plans.filter(p => p.track === "full");
  const accountingPlans = plans.filter(p => p.track === "accounting");

  const showFull       = track === "all" || track === "full";
  const showAccounting = track === "all" || track === "accounting";

  return (
    <section id="pricing" className="py-20 sm:py-24 px-4 sm:px-6 bg-white">
      <div className="max-w-7xl mx-auto">

        {/* ── headline ── */}
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            Choose the plan that fits your team and portfolio
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            From growing contractors to enterprise finance teams. Purpose-built for project finance — not adapted from generic accounting software. No hidden fees. Cancel any time.
          </p>
        </div>

        {/* ── track toggle ── */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden bg-slate-50 text-sm font-medium">
            {(["all","full","accounting"] as const).map(t => (
              <button
                key={t}
                onClick={() => setTrack(t as typeof track)}
                className="px-4 py-2 transition-all cursor-pointer"
                style={track === t ? { background:"#1e293b", color:"#fff" } : { color:"#64748b" }}
              >
                {t === "all" ? `All ${plans.length} plans` : t === "full" ? "Finance suite" : "Accounting (Pro Plus)"}
              </button>
            ))}
          </div>
        </div>

        {/* ── billing toggle ── */}
        <div className="flex justify-center items-center gap-3 mb-12">
          <span className={`text-sm font-medium ${billing === "monthly" ? "text-slate-900" : "text-slate-400"}`}>Monthly</span>
          <button
            onClick={() => setBilling(b => b === "monthly" ? "annual" : "monthly")}
            className="relative w-12 h-6 rounded-full transition-colors cursor-pointer"
            style={{ background: billing === "annual" ? FULL_COLOR : "#cbd5e1" }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
              style={{ transform: billing === "annual" ? "translateX(24px)" : "translateX(0)" }}
            />
          </button>
          <span className={`text-sm font-medium ${billing === "annual" ? "text-slate-900" : "text-slate-400"}`}>Annual</span>
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full text-white"
            style={{ background: FULL_COLOR }}
          >2 months free</span>
        </div>

        {/* ── loading skeleton ── */}
        {loading && (
          <div className="flex justify-center items-center py-20 text-slate-400 text-sm gap-3">
            <div className="w-5 h-5 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin" />
            Loading plans…
          </div>
        )}

        {/* ── Finance suite plans (Lite / Lite Pro / Pro) ── */}
        {!loading && showFull && corePlans.length > 0 && (
          <div className="mb-10">
            <div
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5"
              style={{ background: "#E1F5EE", color: FULL_COLOR }}
            >
              Full Finance Suite · Invoices · BOQ · Complete document generation
            </div>
            <div className="grid gap-5 sm:grid-cols-3">
              {corePlans.map(p => (
                <PlanCard key={p.id} plan={p} billing={billing} onTryDemo={onTryDemo} />
              ))}
            </div>
          </div>
        )}

        {/* ── Pro Plus / Accounting band ── */}
        {!loading && showAccounting && accountingPlans.length > 0 && (
          <div className="mb-10">
            <div
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5"
              style={{ background: "#D1FAE5", color: ACC_COLOR }}
            >
              Pro Plus · All Features · Double-Entry Accounting · GL · Journal Entries
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1 max-w-lg">
              {accountingPlans.map(p => (
                <PlanCard key={p.id} plan={p} billing={billing} onTryDemo={onTryDemo} />
              ))}
            </div>
          </div>
        )}

        {/* ── compare toggle ── */}
        <div className="text-center mb-6">
          <button
            onClick={() => setCompareOpen(o => !o)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
          >
            {compareOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            ↕ Compare all features
          </button>
        </div>

        {/* ── feature comparison table ── */}
        {compareOpen && (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm mb-10">
            <table className="w-full min-w-[860px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="py-3 px-5 text-left text-slate-500 font-semibold w-48">Feature</th>
                  {COL_PLANS.map((label, i) => (
                    <th
                      key={label}
                      className="py-3 px-3 text-center text-xs font-bold"
                      style={{ color: i < 3 ? LITE_COLOR : FULL_COLOR }}
                    >{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_GROUPS.map((grp, gi) => (
                  <React.Fragment key={`grp-${gi}`}>
                    <tr className="bg-slate-50/80">
                      <td colSpan={5} className="px-5 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                        {grp.label}
                      </td>
                    </tr>
                    {grp.rows.map((row, ri) => (
                      <tr key={`r-${gi}-${ri}`} className={`border-b border-slate-100 ${ri % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                        <td className="py-3 px-5 text-slate-600 text-sm">{row.label}</td>
                        {row.values.map((v, vi) => (
                          <td key={vi} className="py-3 px-3 text-center">
                            <Cell v={v} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── bottom note ── */}
        <div className="mt-8 rounded-xl border border-slate-100 bg-slate-50 p-6 text-sm text-slate-500 leading-relaxed max-w-3xl mx-auto text-center">
          <p className="mb-2">
            <strong className="text-slate-700">Lite</strong> — full document generation, invoicing, BOQ, VOs,
            subcontractor management, cash flow, and payroll. Best for established contractors.
          </p>
          <p className="mb-2">
            <strong className="text-slate-700">Lite Pro</strong> — adds financial reports, CFO View, retention
            management, and promissory notes / PDC tracking.
          </p>
          <p className="mb-2">
            <strong className="text-slate-700">Pro</strong> — adds multi-company workspace, API access,
            custom branding, and advanced VAT configuration.
          </p>
          <p className="mb-2">
            <strong className="text-slate-700">Pro Plus</strong> — all Pro features plus a dedicated
            double-entry accounting module (Chart of Accounts, Journal Entries, General Ledger, Trial Balance,
            Balance Sheet, Income Statement, Party Statements).
          </p>
          <p className="text-xs text-slate-400">
            All prices shown in USD. No hidden fees. Cancel any time.
          </p>
        </div>

      </div>
    </section>
  );
}
